import React, { useEffect, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
library.add(fas)

import { getRequest } from "@react/helpers/fetch"
import { rememberEntity } from "@react/helpers/entity_filter"

import Header           from "./components/Header"
import FilterBar        from "./components/FilterBar"
import KpiStrip         from "./components/KpiStrip"
import PortfolioBridge  from "./components/PortfolioBridge"
import BillingPlanChart from "./components/BillingPlanChart"
import ProjectionChart  from "./components/ProjectionChart"
import RetentionCard    from "./components/RetentionCard"
import SegmentsCard     from "./components/SegmentsCard"
import TopClientsChart  from "./components/TopClientsChart"
import BreakdownPie     from "./components/BreakdownPie"
import ExpiryChart      from "./components/ExpiryChart"
import AnomaliesCard    from "./components/AnomaliesCard"

const buildQuery = (filters) => {
  const params = new URLSearchParams()
  if (filters.year)    params.set("year", filters.year)
  if (filters.partner) params.set("partner", filters.partner)
  if (filters.rolling) params.set("rolling", "true")
  if (filters.p3_only) params.set("p3_only", "true")
  ;(filters.client_ids    || []).forEach((id) => params.append("client_ids[]", id))
  ;(filters.category_ids  || []).forEach((id) => params.append("category_ids[]", id))
  ;(filters.formula_ids   || []).forEach((id) => params.append("formula_ids[]", id))
  ;(filters.periodicities || []).forEach((v)  => params.append("periodicities[]", v))
  Object.entries(filters.field_answers || {}).forEach(([id, value]) => {
    if (value) params.set(`field_answers[${id}]`, value)
  })
  return params.toString()
}

const App = ({ data: initialData, json_url, back_url, anomalies_url }) => {
  const [data, setData]       = useState(initialData || {})
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState(initialData?.filters || {})

  useEffect(() => {
    document.getElementById("contracts-dashboard-boot")?.remove()
  }, [])

  const handleFiltersChange = (next) => {
    if (data.partner_filter) rememberEntity(data.partner_filter.param, next.partner)
    setFilters(next)
    setLoading(true)
    const qs = buildQuery(next)
    getRequest(`${json_url}${qs ? `?${qs}` : ""}`)
      .then((response) => {
        if (response && !response.errors) {
          setData(response)
          setFilters(response.filters)
        }
      })
      .finally(() => setLoading(false))
  }

  const charts   = data.charts || {}
  const currency = data.currency

  return (
    <div className="pb-10">
      <Header backUrl={back_url} />

      <FilterBar filters={filters} options={data.filter_options || {}}
                 partnerFilter={data.partner_filter}
                 loading={loading} onChange={handleFiltersChange} />

      <KpiStrip kpis={data.kpis} quality={data.quality} currency={currency} />

      {/* Le portefeuille dans le temps, puis le cash qu'il produit. */}
      <div className="px-7 mt-4 grid gap-4 grid-cols-1 xl:grid-cols-2">
        <PortfolioBridge  data={charts.portfolio_evolution} currency={currency} />
        <BillingPlanChart data={charts.billing_plan}        currency={currency} />
      </div>

      <div className="px-7 mt-4">
        <ProjectionChart data={charts.projection} currency={currency} />
      </div>

      {/* Ce que la base devient : rétention, concentration, anomalies. */}
      <div className="px-7 mt-4 grid gap-4 grid-cols-1 xl:grid-cols-3">
        <RetentionCard data={charts.retention} />
        <SegmentsCard  data={charts.size_segments} currency={currency} />
        <AnomaliesCard quality={data.quality} anomaliesUrl={anomalies_url} partner={filters.partner} />
      </div>

      {/* Les axes de répartition. */}
      <div className="px-7 mt-4 grid gap-4 grid-cols-1 xl:grid-cols-2">
        <TopClientsChart data={charts.top_clients} currency={currency} />
        <ExpiryChart     data={charts.expiry}      currency={currency} />
      </div>

      <div className="px-7 mt-4 grid gap-4 grid-cols-1 lg:grid-cols-3">
        <BreakdownPie icon="tags" data={charts.by_category} currency={currency}
                      title={I18n.t("contracts_dashboard.charts.by_category")}
                      subtitle={I18n.t("contracts_dashboard.charts.by_category_hint")} />
        <BreakdownPie icon="percent" data={charts.by_formula} currency={currency}
                      title={I18n.t("contracts_dashboard.charts.by_formula")}
                      subtitle={I18n.t("contracts_dashboard.charts.by_formula_hint")} />
        <BreakdownPie icon="repeat" data={charts.by_periodicity} currency={currency}
                      title={I18n.t("contracts_dashboard.charts.by_periodicity")}
                      subtitle={I18n.t("contracts_dashboard.charts.by_periodicity_hint")} />
      </div>
    </div>
  )
}

export default App
